import { RE_TENDER_DIARY_MAX_MONTHS, type ReTenderChallenger } from '../../api/tenders';

/** Months between two YYYY-MM keys, inclusive. */
export function monthsInclusive(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number) as [number, number];
  const [ty, tm] = to.split('-').map(Number) as [number, number];
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** The "to" month kept inside [from, from + max − 1]. */
export function clampDiaryTo(from: string, to: string, max = RE_TENDER_DIARY_MAX_MONTHS): string {
  const span = monthsInclusive(from, to);
  if (span >= 1 && span <= max) return to;
  const [fy, fm] = from.split('-').map(Number) as [number, number];
  const offset = span < 1 ? 0 : max - 1;
  const d = new Date(Date.UTC(fy, fm - 1 + offset, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The YYYY-MM key `months` from the current month (UTC). `now` is for tests. */
export function monthOffset(months: number, now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The rule that picked a challenger, short enough for a chip: "lost bid",
 * "panel ×2", "won 14 similar".
 */
export function challengerRule(p: Pick<ReTenderChallenger, 'basis' | 'awards'>): string {
  if (p.basis === 'lost_bid') return 'lost bid';
  if (p.basis === 'panel') return p.awards && p.awards > 1 ? `panel ×${p.awards}` : 'panel';
  return p.awards ? `won ${p.awards} similar` : 'wins similar';
}


/**
 * CPV divisions the tender pipeline sources for (50 repair, 55 catering, 72 IT
 * services, 79 business services, 90 facility services): the work a bid desk
 * answers. Anything else (floating-car data, POS and medical software, storage
 * hardware, construction) is public-sector noise for a bid-team audience.
 */
export const BID_RELEVANT_CPV_DIVISIONS: ReadonlySet<string> = new Set(['50', '55', '72', '79', '90']);

/** True when a contract's main CPV division is one the pipeline sources for. An unknown division is kept (never hidden on a guess). */
export function isBidRelevantCpv(cpvDivision: string | null | undefined): boolean {
  return !cpvDivision || BID_RELEVANT_CPV_DIVISIONS.has(cpvDivision);
}

/** True when an end date (YYYY-MM-DD…) falls on or before the window's last day (YYYY-MM-DD…). */
export function endsWithin(endDate: string | null | undefined, windowTo: string): boolean {
  return !!endDate && endDate.slice(0, 10) <= windowTo.slice(0, 10);
}
